import { useDatasetsByTeamId, useDatasetById } from '@/queries/datasets';
import DatabaseOutlined from '@ant-design/icons/DatabaseOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import SearchOutlined from '@ant-design/icons/SearchOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Input from 'antd/es/input';
import Select from 'antd/es/select';
import Typography from 'antd/es/typography';
import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';

import type { DatasetSummary } from '@mai/platform-api/src/routes/datasets.schema';
import type { JSONSchemaType } from '@mai/types';

const { Text } = Typography;

const SelectContainer = styled.div`
  ${({ theme }) => `
  position: relative;
  
  .ant-select {
    width: 100%;
  }
  
  .ant-select-selection-item {
    display: flex;
    align-items: center;
    gap: ${theme.marginXS}px;
  }
`}
`;

const DatasetInfo = styled.div`
  ${({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.marginSM}px;
  padding: ${theme.paddingSM}px ${theme.padding}px;
  background: ${theme.colorFillQuaternary};
  border-radius: ${theme.borderRadius}px;
  margin-top: ${theme.marginSM}px;
`}
`;

const DatasetDetails = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 400px;
  overflow: hidden;
`;

const DatasetName = styled(Text)`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DatasetMeta = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
  color: ${theme.colorTextSecondary};
`}
`;

const EmptyState = styled.div`
  ${({ theme }) => `
  padding: ${theme.paddingLG}px;
  text-align: center;
  color: ${theme.colorTextSecondary};
  font-size: ${theme.fontSize}px;
  border: ${theme.lineWidth}px dashed ${theme.colorBorder};
  border-radius: ${theme.borderRadius}px;
  background: ${theme.colorBgContainer};
`}
`;

interface DatasetSelectFieldProps {
  value?: string; // dataset_id
  onChange?: (value: string | undefined) => void;
  disabled?: boolean;
  readonly?: boolean;
  teamId: string;
  schema?: JSONSchemaType & {
    description?: string;
  };
  placeholder?: string;
}

const DatasetSelectFieldInner: React.FC<DatasetSelectFieldProps> = ({
  value,
  onChange,
  disabled = false,
  readonly = false,
  teamId,
  schema,
  placeholder = 'Select a dataset',
}) => {
  const { message } = App.useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();

  // Fetch datasets for the team
  const { data: datasetsResponse, isLoading: isLoadingDatasets } = useDatasetsByTeamId(teamId);
  
  // Fetch details of the selected dataset
  const { data: selectedDataset } = useDatasetById(value || null, { enabled: !!value });

  const datasets = datasetsResponse?.items || [];
  
  // Filter datasets based on search term
  const filteredDatasets = datasets.filter((dataset: DatasetSummary) =>
    dataset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dataset.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDatasetSelect = (datasetId: string) => {
    onChange?.(datasetId);
    void message.success('Dataset selected successfully!');
  };

  const handleRemove = () => {
    onChange?.(undefined);
    void message.success('Dataset selection cleared!');
  };


  // Show selected dataset info if we have a selection
  if (value && selectedDataset) {
    return (
      <>
        <DatasetInfo>
          <DatabaseOutlined style={{ fontSize: theme.fontSizeLG, color: theme.colorPrimary }} />
          <DatasetDetails>
            <DatasetName strong>{selectedDataset.name}</DatasetName>
            <DatasetMeta>
              {/* Dataset metadata can be displayed here */}
            </DatasetMeta>
          </DatasetDetails>
          {!disabled && !readonly && (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemove}
              size="small"
            >
              Remove
            </Button>
          )}
        </DatasetInfo>
      </>
    );
  }

  // Show loading state
  if (isLoadingDatasets) {
    return (
      <EmptyState>
        <LoadingOutlined /> Loading datasets...
      </EmptyState>
    );
  }

  // Show empty state if no datasets
  if (datasets.length === 0) {
    return (
      <EmptyState>
        <DatabaseOutlined style={{ fontSize: theme.fontSizeLG, marginBottom: theme.marginSM }} />
        <div>No datasets available</div>
        <Text type="secondary" style={{ fontSize: theme.fontSizeSM }}>
          Create a dataset first to use it in pipelines
        </Text>
      </EmptyState>
    );
  }

  return (
    <SelectContainer>
      <Select
        placeholder={placeholder}
        value={value}
        onChange={handleDatasetSelect}
        disabled={disabled || readonly}
        showSearch
        searchValue={searchTerm}
        onSearch={setSearchTerm}
        filterOption={false}
        optionFilterProp="children"
        dropdownRender={(menu) => (
          <div>
            <div style={{ padding: theme.paddingXS }}>
              <Input
                placeholder="Search datasets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
                size="small"
              />
            </div>
            {menu}
          </div>
        )}
        options={filteredDatasets.map((dataset: DatasetSummary) => ({
          value: dataset.datasetId,
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.marginXS }}>
              <DatabaseOutlined style={{ color: theme.colorPrimary }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: theme.fontWeightStrong }}>
                  {dataset.name}
                </div>
                <div style={{ 
                  fontSize: theme.fontSizeSM, 
                  color: theme.colorTextSecondary
                }}>
                  {/* Dataset metadata can be displayed here */}
                </div>
              </div>
            </div>
          ),
        }))}
        notFoundContent={
          searchTerm ? (
            <div style={{ padding: theme.paddingSM, textAlign: 'center' }}>
              <Text type="secondary">No datasets found matching "{searchTerm}"</Text>
            </div>
          ) : (
            <div style={{ padding: theme.paddingSM, textAlign: 'center' }}>
              <Text type="secondary">No datasets available</Text>
            </div>
          )
        }
      />
      {schema?.description && (
        <Text type="secondary" style={{ fontSize: theme.fontSizeSM, marginTop: theme.marginXS, display: 'block' }}>
          {schema.description}
        </Text>
      )}
    </SelectContainer>
  );
};

// Export wrapper that works with Ant Design Form.Item
export const DatasetSelectField = React.forwardRef<
  HTMLDivElement,
  DatasetSelectFieldProps & {
    value?: string;
    onChange?: (value: string | undefined) => void;
  }
>((props, ref) => {
  return (
    <div ref={ref}>
      <DatasetSelectFieldInner {...props} />
    </div>
  );
});

DatasetSelectField.displayName = 'DatasetSelectField';